import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Row, Col, Container, Button, Modal, Badge, Form } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { config } from "../../config";
import { AppContext } from "../../AppContext/Context";
import Loader from "../loader";
import useHandleUnauthorized from "../../hooks/useHandleUnauthorized/UseHandleUnauthorized";
import SweetLayout from "../../Layout/SweetLayout";
import Select from "react-select";
import debounce from "lodash/debounce";
import "../custom/table/styles.css";

const ACCOUNT_CACHE_KEY = "admin_accounts_cache";

const normalizeAccount = (account) => {
  if (!account || typeof account !== "object") {
    return null;
  }
  return {
    account_id: account.account_id ?? account.id,
    name: account.name ?? account.account_name ?? "",
    owner_user_id: account.owner_user_id ?? account.owner ?? account.account_owner ?? "",
  };
};

const readAccountsFromCache = () => {
  try {
    const cached = sessionStorage.getItem(ACCOUNT_CACHE_KEY);
    if (!cached) {
      return null;
    }
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed.map(normalizeAccount).filter(Boolean) : null;
  } catch (error) {
    console.warn("Failed to read accounts from cache", error);
    return null;
  }
};

const writeAccountsToCache = (accounts) => {
  try {
    const normalized = Array.isArray(accounts)
      ? accounts.map(normalizeAccount).filter(Boolean)
      : [];
    sessionStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(normalized));
  } catch (error) {
    console.warn("Failed to write accounts to cache", error);
  }
};

const UserTab = () => {
  const { isLoading, getIdTokenClaims, user, isAuthenticated } = useAuth0();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const { setIsLoad, isLoad } = useContext(AppContext);
  
  // Account state (like ResponseList.tsx)
  const [accounts, setAccounts] = useState(() => {
    const cached = readAccountsFromCache();
    return cached || [];
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const hasFetchedAccounts = useRef(false);
  
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const recordsPerPage = 10;
  const handleUnauthorized = useHandleUnauthorized();
  
  // Modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  
  // Create user modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: "",
    groups: [],
    invitation_message: "",
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  const [createUserSuccess, setCreateUserSuccess] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (error) {
      return "N/A";
    }
  };

  const fetchAccounts = useCallback(async () => {
    // Check cache first
    const cached = readAccountsFromCache();
    if (cached && cached.length > 0) {
      console.log("UsersTab: Using cached accounts", cached.length);
      setAccounts(cached);
      hasFetchedAccounts.current = true;
      return;
    }

    try {
      setLoadingAccounts(true);
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      };
      const path = `/account`;
      const url = `${config.baseURL}/admin/v1${path}`;
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const normalized = Array.isArray(result)
        ? result.map(normalizeAccount).filter(Boolean)
        : [];
      setAccounts(normalized);
      writeAccountsToCache(normalized);
      setLoadingAccounts(false);
      hasFetchedAccounts.current = true;
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setLoadingAccounts(false);
    }
  }, [getIdTokenClaims]);

  // Fetch accounts on mount (like ResponseList.tsx)
  useEffect(() => {
    if (user && !hasFetchedAccounts.current && !loadingAccounts) {
      // If accounts already loaded from cache, mark as fetched
      if (accounts.length > 0) {
        hasFetchedAccounts.current = true;
        return;
      }
      fetchAccounts();
    }
  }, [user, loadingAccounts, fetchAccounts, accounts.length]);

  const handleAccountChange = (selectedOption) => {
    setSelectedAccount(selectedOption);
    setCurrentPage(1);
    setSearchQuery("");
  };

  const fetchUserDetails = async (email) => {
    if (!email || !selectedAccount?.value) return;
    
    setLoadingUserDetails(true);
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken.__raw;

      const params = new URLSearchParams({
        include_roles: "true",
        account_id: String(selectedAccount.value),
        include_account_info: "true",
      });

      const endpoint = `${config.baseURL}/admin/v1/user/${encodeURIComponent(email)}?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      setUserDetails(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error.message);
      handleUnauthorized(error);
      setUserDetails(null);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleViewDetails = (email) => {
    setShowUserModal(true);
    fetchUserDetails(email);
  };

  const handleCloseModal = () => {
    setShowUserModal(false);
    setUserDetails(null);
  };

  const handleOpenCreateUserModal = () => {
    setShowCreateUserModal(true);
    setCreateUserForm({
      email: "",
      groups: [],
      invitation_message: "",
    });
    setCreateUserError(null);
    setCreateUserSuccess(false);
  };

  const handleCloseCreateUserModal = () => {
    setShowCreateUserModal(false);
    setCreateUserForm({
      email: "",
      groups: [],
      invitation_message: "",
    });
    setCreateUserError(null);
    setCreateUserSuccess(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!selectedAccount?.value) {
      setCreateUserError("Please select an account first");
      return;
    }

    if (!createUserForm.email) {
      setCreateUserError("Email is required");
      return;
    }

    setCreatingUser(true);
    setCreateUserError(null);
    setCreateUserSuccess(false);

    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken.__raw;

      const payload = {
        email: createUserForm.email,
        groups: createUserForm.groups.map(g => g.value),
        invitation_message: createUserForm.invitation_message,
        account_id: selectedAccount.value,
      };

      const endpoint = `${config.baseURL}/admin/v1/user`;

      await axios.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      setCreateUserSuccess(true);
      
      // Refresh user list after a short delay
      setTimeout(() => {
        handleCloseCreateUserModal();
        // Trigger refresh
        setRefreshTrigger(prev => prev + 1);
        setCurrentPage(1);
      }, 1500);
    } catch (error) {
      console.error("Error creating user:", error);
      setCreateUserError(
        error.response?.data?.message || error.message || "Failed to create user"
      );
      handleUnauthorized(error);
    } finally {
      setCreatingUser(false);
    }
  };

  const groupOptions = [
    { value: "Admin", label: "Admin" },
    { value: "User", label: "User" },
    { value: "AI Sales Pro", label: "AI Sales Pro" },
    { value: "AIPerformanceManager", label: "AIPerformanceManager" },
  ];

  useEffect(() => {
    if (user && user.ept_roles) {
      if (
        !user.ept_roles.includes("EptAdmin") &&
        !user.ept_roles.includes("EptViewer")
      ) {
        setShowErrorMessage(true);
      } else {
        setShowErrorMessage(false);
      }
    }
  }, [user, isAuthenticated, isLoading]);

  useEffect(() => {
    if (!selectedAccount?.value || isLoading) {
      setUsers([]);
      setTotalCount(0);
      setCurrentPage(1);
      return;
    }

    const fetchUsers = async () => {
      setIsLoad(true);
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken.__raw;

        const params = new URLSearchParams({
          format: "gridjs",
          page: String(currentPage),
          resultsPerPage: String(recordsPerPage),
          include_roles: "true",
          include_account_info: "false",
          account_id: String(selectedAccount.value),
        });

        if (searchQuery && searchQuery.trim()) {
          params.set("query", searchQuery.trim());
        }

        const endpoint = `${config.baseURL}/admin/v1/user_list?${params.toString()}`;

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        // Handle paginated response format: { count, next, previous, results }
        if (response.data && response.data.results) {
          setUsers(response.data.results || []);
          setTotalCount(response.data.count || 0);
        } else if (Array.isArray(response.data)) {
          setUsers(response.data);
          setTotalCount(response.data.length);
        } else {
          setUsers([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("API request error:", error.message);
        handleUnauthorized(error);
        setUsers([]);
        setTotalCount(0);
      } finally {
        setIsLoad(false);
      }
    };

    const debouncedFetchUsers = debounce(fetchUsers, 500);
    debouncedFetchUsers();

    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [selectedAccount?.value, currentPage, refreshTrigger, searchQuery, isLoading, getIdTokenClaims, setIsLoad, handleUnauthorized]);

  if (!selectedAccount) {
    return (
      <SweetLayout>
        <Container fluid="xxl">
          <div style={{ marginTop: "40px" }}>
            <Row>
              <Col md={8} lg={6} className="mx-auto">
                <div
                  style={{
                    background: "#ffffff",
                    padding: "32px",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
                  }}
                >
                  <h2
                    style={{
                      marginBottom: "24px",
                      fontSize: "24px",
                      fontWeight: "600",
                    }}
                  >
                    Users
                  </h2>
                  <p
                    style={{
                      marginBottom: "24px",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    Select an account to view users.
                  </p>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                      }}
                    >
                      Account
                    </label>
                    <Select
                      id="select-account"
                      onChange={handleAccountChange}
                      value={selectedAccount}
                      options={(accounts || []).map((account) => ({
                        value: account.account_id,
                        label: `${account.account_id} - ${account.name} - ${account.owner_user_id}`,
                      }))}
                      isSearchable
                      placeholder="Select an account..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "42px",
                          borderColor: "#D1D5DB",
                        }),
                      }}
                      isLoading={loadingAccounts}
                    />
                  </div>

                  {loadingAccounts && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6b7280",
                      }}
                    >
                      Loading accounts...
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </SweetLayout>
    );
  }

  return (
    <SweetLayout>
      <Container fluid>
        {showErrorMessage ? (
          <div
            className="border border-dark d-flex align-items-center justify-content-center fw-bold"
            style={{ minHeight: "300px" }}
          >
            You are not authorized to use this app. Please contact your ept AI
            administrator if you believe you should have access.
          </div>
        ) : (
          <div>
            <div
              style={{
                background: "#f9fafb",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#374151",
                }}
              >
                Account
              </label>
              <Select
                id="select-account"
                onChange={handleAccountChange}
                value={selectedAccount}
                options={(accounts || []).map((account) => ({
                  value: account.account_id,
                  label: `${account.account_id} - ${account.name} - ${account.owner_user_id}`,
                }))}
                isSearchable
                placeholder="Select an account..."
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "42px",
                    borderColor: "#D1D5DB",
                  }),
                }}
              />
            </div>
            <Row className="mb-3 d-flex align-items-center">
              <Col xs={12} md={6}>
                <h1>Users</h1>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0">
                <Button
                  variant="dark"
                  onClick={handleOpenCreateUserModal}
                >
                  Create New User
                </Button>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Control
                  type="text"
                  placeholder="Search users by email or name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ maxWidth: "500px" }}
                />
              </Col>
            </Row>

            <div className="ept-data-table">
              <div style={{ overflowX: "auto", position: "relative" }}>
                {isLoad && (
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      width: "100%",
                      zIndex: 10,
                    }}
                  >
                    <Loader />
                  </div>
                )}

                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Roles</th>
                        <th>Last Login</th>
                        <th>Created Date</th>
                        <th>Updated Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 && !isLoad ? (
                        <tr>
                          <td colSpan={6} className="text-center">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        users.map((userData, index) => (
                          <tr key={userData.user_id || userData.email || index}>
                            <td>{userData.email || "N/A"}</td>
                            <td>
                              {userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {userData.roles.map((role, idx) => (
                                    <span
                                      key={idx}
                                      className="badge bg-primary"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {role}
                                    </span>
                                  ))}
                                </div>
                              ) : userData.groups && Array.isArray(userData.groups) && userData.groups.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {userData.groups.map((group, idx) => (
                                    <span
                                      key={idx}
                                      className="badge bg-primary"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {group}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>{formatDate(userData.last_login_date)}</td>
                            <td>{formatDate(userData.created_date)}</td>
                            <td>{formatDate(userData.updated_date)}</td>
                            <td>
                              <span
                                className="text-primary text-nowrap"
                                onClick={() => handleViewDetails(userData.email)}
                                style={{ cursor: "pointer" }}
                              >
                                View Details
                                <span className="bi bi-chevron-right ms-1"></span>
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalCount > 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    <div className="btn-group" role="group">
                      <Button
                        variant={currentPage === 1 ? "outline-secondary" : "secondary"}
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <span className="btn btn-outline-secondary disabled">
                        Page {currentPage} of {Math.ceil(totalCount / recordsPerPage)} ({totalCount} total)
                      </span>
                      <Button
                        variant={currentPage >= Math.ceil(totalCount / recordsPerPage) ? "outline-secondary" : "secondary"}
                        disabled={currentPage >= Math.ceil(totalCount / recordsPerPage)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Container>

      {/* User Details Modal */}
      <Modal show={showUserModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingUserDetails ? (
            <div className="d-flex justify-content-center py-4">
              <Loader />
            </div>
          ) : userDetails ? (
            <div className="ept-data-table">
              <div className="table-container">
                <table>
                  <tbody>
                    <tr>
                      <th style={{ width: "30%" }}>Email</th>
                      <td>{userDetails.email || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Name</th>
                      <td>{userDetails.name || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>User ID</th>
                      <td>{userDetails.user_id || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Account ID</th>
                      <td>{userDetails.account_id || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Roles</th>
                      <td>
                        {userDetails.roles && Array.isArray(userDetails.roles) && userDetails.roles.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {userDetails.roles.map((role, idx) => (
                              <Badge key={idx} bg="primary" style={{ fontSize: "0.875rem" }}>
                                {role}
                              </Badge>
                            ))}
                          </div>
                        ) : userDetails.groups && Array.isArray(userDetails.groups) && userDetails.groups.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {userDetails.groups.map((group, idx) => (
                              <Badge key={idx} bg="primary" style={{ fontSize: "0.875rem" }}>
                                {group}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Groups</th>
                      <td>
                        {userDetails.groups && Array.isArray(userDetails.groups) && userDetails.groups.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {userDetails.groups.map((group, idx) => (
                              <Badge key={idx} bg="info" style={{ fontSize: "0.875rem" }}>
                                {group}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Created Date</th>
                      <td>{formatDate(userDetails.created_date)}</td>
                    </tr>
                    <tr>
                      <th>Updated Date</th>
                      <td>{formatDate(userDetails.updated_date)}</td>
                    </tr>
                    <tr>
                      <th>Last Login Date</th>
                      <td>{formatDate(userDetails.last_login_date)}</td>
                    </tr>
                    <tr>
                      <th>Logins Count</th>
                      <td>{userDetails.logins_count ?? "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Email Verified</th>
                      <td>{userDetails.email_verified ? "Yes" : "No"}</td>
                    </tr>
                    <tr>
                      <th>SSO User</th>
                      <td>{userDetails.is_sso_user ? "Yes" : "No"}</td>
                    </tr>
                    <tr>
                      <th>Invited Not Accepted</th>
                      <td>{userDetails.invited_not_accepted ? "Yes" : "No"}</td>
                    </tr>
                    {userDetails.account_info && (
                      <tr>
                        <th>Account Info</th>
                        <td>
                          <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.875rem" }}>
                            {JSON.stringify(userDetails.account_info, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p>No user details found</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create User Modal */}
      <Modal show={showCreateUserModal} onHide={handleCloseCreateUserModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateUser}>
          <Modal.Body>
            {createUserSuccess ? (
              <div className="text-center py-4">
                <div className="text-success mb-3">
                  <i className="bi bi-check-circle-fill" style={{ fontSize: "3rem" }}></i>
                </div>
                <h5>User created successfully!</h5>
                <p className="text-muted">The user has been added to the account.</p>
              </div>
            ) : (
              <>
                {createUserError && (
                  <div className="alert alert-danger" role="alert">
                    {createUserError}
                  </div>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email address"
                    value={createUserForm.email}
                    onChange={(e) =>
                      setCreateUserForm({ ...createUserForm, email: e.target.value })
                    }
                    required
                    disabled={creatingUser}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Groups</Form.Label>
                  <Select
                    isMulti
                    options={groupOptions}
                    value={createUserForm.groups}
                    onChange={(selected) =>
                      setCreateUserForm({ ...createUserForm, groups: selected || [] })
                    }
                    placeholder="Select groups..."
                    isDisabled={creatingUser}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "42px",
                      }),
                    }}
                  />
                  <Form.Text className="text-muted">
                    Select one or more groups for the user
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Invitation Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter invitation message (optional)"
                    value={createUserForm.invitation_message}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        invitation_message: e.target.value,
                      })
                    }
                    disabled={creatingUser}
                  />
                  <Form.Text className="text-muted">
                    Optional message to include in the invitation email
                  </Form.Text>
                </Form.Group>

                <div className="alert alert-info mb-0" role="alert">
                  <strong>Account:</strong> {selectedAccount?.label || "N/A"}
                  <br />
                  <small>The user will be added to the selected account automatically.</small>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!createUserSuccess && (
              <>
                <Button variant="secondary" onClick={handleCloseCreateUserModal} disabled={creatingUser}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={creatingUser}>
                  {creatingUser ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating...
                    </>
                  ) : (
                    "Create User"
                  )}
                </Button>
              </>
            )}
            {createUserSuccess && (
              <Button variant="primary" onClick={handleCloseCreateUserModal}>
                Close
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </SweetLayout>
  );
};

export default UserTab;
